'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Search, Bookmark, MessageSquare, Lightbulb, Users, ChevronDown, ChevronUp, AlertCircle, MapPin, Calendar, ImagePlus, X, Plus, Briefcase, ExternalLink, Tag, Zap } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import SolutionCard from './SolutionCard';
import AISmartSearch from './AISmartSearch';
import type { Problem, Solution } from './mockData';
import type { FilterState } from './BoardScreen';
import { useAuth } from '@/contexts/AuthContext';

const TAB_OPTIONS: { id: FilterState['tab']; label: string }[] = [
  { id: 'problems', label: 'Problems' },
  { id: 'solutions', label: 'Solutions' },
  { id: 'funded', label: 'Funded' },
];

const CONTINENTS = [
  'Africa', 'Antarctica', 'Asia', 'Europe',
  'North America', 'Oceania', 'South America',
];

export const PROBLEM_CATEGORIES = [
  'AgriTech',
  'HealthTech',
  'Fintech',
  'EdTech',
  'Clean Energy',
  'Supply Chain',
  'LegalTech',
  'ClimateTech',
  'IT & Software',
  'Infrastructure',
  'Water & Sanitation',
  'Transport',
  'General',
] as const;

export type ProblemCategory = typeof PROBLEM_CATEGORIES[number];

const CATEGORY_COLORS: Record<string, string> = {
  'AgriTech': 'bg-green-100 text-green-700',
  'HealthTech': 'bg-red-100 text-red-700',
  'Fintech': 'bg-blue-100 text-blue-700',
  'EdTech': 'bg-purple-100 text-purple-700',
  'Clean Energy': 'bg-yellow-100 text-yellow-700',
  'Supply Chain': 'bg-orange-100 text-orange-700',
  'LegalTech': 'bg-indigo-100 text-indigo-700',
  'ClimateTech': 'bg-teal-100 text-teal-700',
  'IT & Software': 'bg-cyan-100 text-cyan-700',
  'Infrastructure': 'bg-stone-100 text-stone-700',
  'Water & Sanitation': 'bg-sky-100 text-sky-700',
  'Transport': 'bg-amber-100 text-amber-700',
  'General': 'bg-gray-100 text-gray-700',
};

// Trending categories shown as quick-filter pills
const TRENDING_CATEGORIES = ['Clean Energy', 'Supply Chain', 'IT & Software', 'AgriTech', 'HealthTech', 'Fintech'];

function wordCount(text: string): number {
  return text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
}

function getTodayISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

interface PostFormData {
  problem: string;
  category: string;
  town: string;
  country: string;
  continent: string;
  dateOccurred: string;
  dateFuture: string;
  affectedGroup: string;
  effects: string;
  workaround: string;
  proposedSolution: string;
  picture: File | null;
}

interface ProblemFeedProps {
  problems: Problem[];
  solutions: Solution[];
  selectedId: string | null;
  onSelect: (p: Problem) => void;
  filters: FilterState;
  updateFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;
  onAddProblem: (p: Problem) => void;
  onOpenEntrepreneurForm: () => void;
  onOpenStandaloneSolutionForm: () => void;
  onProposeSolutionForProblem: (problem: Problem) => void;
}

export default function ProblemFeed({
  problems,
  solutions,
  selectedId,
  onSelect,
  filters,
  updateFilter,
  onAddProblem,
  onOpenEntrepreneurForm,
  onOpenStandaloneSolutionForm,
  onProposeSolutionForProblem,
}: ProblemFeedProps) {
  const today = getTodayISO();
  const router = useRouter();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof PostFormData, string>>>({});
  const [aiSearchOpen, setAiSearchOpen] = useState(false);
  const [aiResults, setAiResults] = useState<{ problems: Problem[]; solutions: Solution[] } | null>(null);
  const [form, setForm] = useState<PostFormData>({
    problem: '',
    category: '',
    town: '',
    country: '',
    continent: '',
    dateOccurred: today,
    dateFuture: '',
    affectedGroup: '',
    effects: '',
    workaround: '',
    proposedSolution: '',
    picture: null,
  });

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  function handleChange(field: keyof PostFormData, value: string) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: '' }));
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    if (file) {
      setForm(prev => ({ ...prev, picture: file }));
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  }

  function removeImage() {
    setForm(prev => ({ ...prev, picture: null }));
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function validate(): boolean {
    const errs: Partial<Record<keyof PostFormData, string>> = {};
    if (!form.problem.trim()) errs.problem = 'Required.';
    else if (wordCount(form.problem) > 50) errs.problem = 'Max 50 words.';
    if (!form.category) errs.category = 'Required.';
    if (!form.town.trim()) errs.town = 'Required.';
    if (!form.country.trim()) errs.country = 'Required.';
    if (!form.continent) errs.continent = 'Required.';
    if (!form.affectedGroup.trim()) errs.affectedGroup = 'Required.';
    else if (wordCount(form.affectedGroup) > 50) errs.affectedGroup = 'Max 50 words.';
    if (!form.effects.trim()) errs.effects = 'Required.';
    if (!form.workaround.trim()) errs.workaround = 'Required.';
    if (!form.proposedSolution.trim()) errs.proposedSolution = 'Required.';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) {
      router.push('/sign-up-login-screen');
      return;
    }
    if (!validate()) return;

    const chosenCategory = form.category || 'General';
    const newProblem: Problem = {
      id: `prob-user-${Date.now()}`,
      title: form.problem.length > 80 ? form.problem.slice(0, 80) + '…' : form.problem,
      category: chosenCategory,
      categoryColor: CATEGORY_COLORS[chosenCategory] ?? 'bg-gray-100 text-gray-700',
      status: 'open',
      poster: 'You',
      posterReputation: 50,
      posterRole: 'poster',
      postedAt: 'Just now',
      postedTimestamp: Date.now(),
      excerpt: form.problem,
      fullDescription: `${form.problem}\n\nAffected Group: ${form.affectedGroup}\n\nEffects: ${form.effects}\n\nCurrent Workaround: ${form.workaround}\n\nProposed Solution: ${form.proposedSolution}\n\nLocation: ${form.town}, ${form.country}, ${form.continent}`,
      fundingTarget: 0,
      fundingRaised: 0,
      solutions: [],
      comments: [],
      bookmarks: 0,
      followers: 0,
      image: previewUrl ?? undefined,
      imageAlt: form.problem,
    };

    onAddProblem(newProblem);
    setForm({
      problem: '',
      category: '',
      town: '',
      country: '',
      continent: '',
      dateOccurred: today,
      dateFuture: '',
      affectedGroup: '',
      effects: '',
      workaround: '',
      proposedSolution: '',
      picture: null,
    });
    setPreviewUrl(null);
    setFormErrors({});
    setFormOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const problemWords = wordCount(form.problem);
  const affectedWords = wordCount(form.affectedGroup);

  const displayProblems = aiResults ? aiResults.problems : problems;
  const displaySolutions = aiResults ? aiResults.solutions : solutions;

  return (
    <div className="flex-1 flex flex-col overflow-hidden border-r border-border" style={{ minWidth: 0 }}>
      {/* Post Problem / Propose Solution Section */}
      <div className="border-b border-border bg-card shrink-0">
        <div className="flex">
          {/* Post a Problem toggle */}
          <button
            onClick={() => {
              if (!user) {
                router.push('/sign-up-login-screen');
                return;
              }
              setFormOpen(v => !v);
            }}
            className="flex-1 flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors border-r border-border"
          >
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-primary flex items-center justify-center">
                <Plus size={13} className="text-primary-foreground" />
              </div>
              <span className="text-sm font-bold text-foreground">Post a Problem</span>
            </div>
            {formOpen ? <ChevronUp size={15} className="text-muted-foreground" /> : <ChevronDown size={15} className="text-muted-foreground" />}
          </button>

          {/* Propose Solution toggle (linked to a problem) */}
          <button
            type="button"
            onClick={() => {
              if (!user) {
                router.push('/sign-up-login-screen');
                return;
              }
              onOpenEntrepreneurForm();
            }}
            className="flex-1 flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors border-r border-border"
          >
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-primary flex items-center justify-center">
                <Briefcase size={13} className="text-primary-foreground" />
              </div>
              <span className="text-sm font-bold text-foreground">Propose Solution</span>
            </div>
            <ChevronDown size={15} className="text-muted-foreground" />
          </button>

          {/* Post a Solution (standalone — no problem required) */}
          <button
            type="button"
            onClick={() => {
              if (!user) {
                router.push('/sign-up-login-screen');
                return;
              }
              onOpenEntrepreneurForm();
            }}
            className="flex-1 flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-success flex items-center justify-center">
                <Zap size={13} className="text-white" />
              </div>
              <span className="text-sm font-bold text-foreground">Propose a Business</span>
            </div>
            <ChevronDown size={15} className="text-muted-foreground" />
          </button>
        </div>

        {formOpen && (
          <form onSubmit={handleSubmit} noValidate className="px-4 pb-4 space-y-3">
            {/* Problem */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide">
                <AlertCircle size={11} className="text-warning" /> Problem <span className="text-danger">*</span>
              </label>
              <textarea
                value={form.problem}
                onChange={e => handleChange('problem', e.target.value)}
                rows={3}
                placeholder="Describe the problem in 50 words or fewer..."
                className={`w-full px-3 py-2 rounded-xl border text-sm text-foreground bg-background placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all ${formErrors.problem ? 'border-danger' : 'border-border'}`}
              />
              <div className="flex justify-between mt-0.5">
                {formErrors.problem ? <p className="text-xs text-danger">{formErrors.problem}</p> : <span />}
                <span className={`text-xs tabular-nums ${problemWords > 50 ? 'text-danger' : 'text-muted-foreground'}`}>{problemWords}/50</span>
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide">
                <Tag size={11} className="text-primary" /> Category <span className="text-danger">*</span>
              </label>
              <select
                value={form.category}
                onChange={e => handleChange('category', e.target.value)}
                className={`w-full px-3 py-2 rounded-xl border text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all ${formErrors.category ? 'border-danger' : 'border-border'}`}
              >
                <option value="">Select a category...</option>
                {PROBLEM_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              {formErrors.category && <p className="text-xs text-danger mt-0.5">{formErrors.category}</p>}
            </div>

            {/* Location */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide">
                <MapPin size={11} className="text-primary" /> Location <span className="text-danger">*</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <input
                    type="text"
                    value={form.town}
                    onChange={e => handleChange('town', e.target.value)}
                    placeholder="Town"
                    className={`w-full px-2.5 py-2 rounded-xl border text-xs text-foreground bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all ${formErrors.town ? 'border-danger' : 'border-border'}`}
                  />
                  {formErrors.town && <p className="text-xs text-danger mt-0.5">{formErrors.town}</p>}
                </div>
                <div>
                  <input
                    type="text"
                    value={form.country}
                    onChange={e => handleChange('country', e.target.value)}
                    placeholder="Country"
                    className={`w-full px-2.5 py-2 rounded-xl border text-xs text-foreground bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all ${formErrors.country ? 'border-danger' : 'border-border'}`}
                  />
                  {formErrors.country && <p className="text-xs text-danger mt-0.5">{formErrors.country}</p>}
                </div>
                <div>
                  <select
                    value={form.continent}
                    onChange={e => handleChange('continent', e.target.value)}
                    className={`w-full px-2.5 py-2 rounded-xl border text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all ${formErrors.continent ? 'border-danger' : 'border-border'}`}
                  >
                    <option value="">Continent</option>
                    {CONTINENTS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {formErrors.continent && <p className="text-xs text-danger mt-0.5">{formErrors.continent}</p>}
                </div>
              </div>
            </div>

            {/* Dates */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide">
                <Calendar size={11} className="text-info" /> Date
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-muted-foreground mb-0.5 block">Occurred</label>
                  <input
                    type="date"
                    value={form.dateOccurred}
                    onChange={e => handleChange('dateOccurred', e.target.value)}
                    className="w-full px-2.5 py-2 rounded-xl border border-border text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-0.5 block">Future (optional)</label>
                  <input
                    type="date"
                    value={form.dateFuture}
                    onChange={e => handleChange('dateFuture', e.target.value)}
                    className="w-full px-2.5 py-2 rounded-xl border border-border text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Affected Group */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide block">
                Affected Group <span className="text-danger">*</span>
              </label>
              <textarea
                value={form.affectedGroup}
                onChange={e => handleChange('affectedGroup', e.target.value)}
                rows={2}
                placeholder="Who is affected? (max 50 words)"
                className={`w-full px-3 py-2 rounded-xl border text-sm text-foreground bg-background placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all ${formErrors.affectedGroup ? 'border-danger' : 'border-border'}`}
              />
              <div className="flex justify-between mt-0.5">
                {formErrors.affectedGroup ? <p className="text-xs text-danger">{formErrors.affectedGroup}</p> : <span />}
                <span className={`text-xs tabular-nums ${affectedWords > 50 ? 'text-danger' : 'text-muted-foreground'}`}>{affectedWords}/50</span>
              </div>
            </div>

            {/* Effects */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide block">
                Effects <span className="text-danger">*</span>
              </label>
              <textarea
                value={form.effects}
                onChange={e => handleChange('effects', e.target.value)}
                rows={2}
                placeholder="What are the effects of this problem?"
                className={`w-full px-3 py-2 rounded-xl border text-sm text-foreground bg-background placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all ${formErrors.effects ? 'border-danger' : 'border-border'}`}
              />
              {formErrors.effects && <p className="text-xs text-danger mt-0.5">{formErrors.effects}</p>}
            </div>

            {/* Workaround */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide block">
                Current Workaround <span className="text-danger">*</span>
              </label>
              <textarea
                value={form.workaround}
                onChange={e => handleChange('workaround', e.target.value)}
                rows={2}
                placeholder="What workarounds are people currently using?"
                className={`w-full px-3 py-2 rounded-xl border text-sm text-foreground bg-background placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all ${formErrors.workaround ? 'border-danger' : 'border-border'}`}
              />
              {formErrors.workaround && <p className="text-xs text-danger mt-0.5">{formErrors.workaround}</p>}
            </div>

            {/* Proposed Solution */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide block">
                Proposed Solution <span className="text-danger">*</span>
              </label>
              <textarea
                value={form.proposedSolution}
                onChange={e => handleChange('proposedSolution', e.target.value)}
                rows={2}
                placeholder="What solution do you propose?"
                className={`w-full px-3 py-2 rounded-xl border text-sm text-foreground bg-background placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all ${formErrors.proposedSolution ? 'border-danger' : 'border-border'}`}
              />
              {formErrors.proposedSolution && <p className="text-xs text-danger mt-0.5">{formErrors.proposedSolution}</p>}
            </div>

            {/* Picture */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide block">Picture (optional)</label>
              {previewUrl ? (
                <div className="relative rounded-xl overflow-hidden h-28 bg-muted">
                  <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"
                  >
                    <X size={12} className="text-white" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-20 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-1.5 hover:border-primary hover:bg-primary/5 transition-all"
                >
                  <ImagePlus size={18} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Click to upload image</span>
                </button>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="w-full py-2.5 bg-primary text-primary-foreground text-sm font-bold rounded-xl hover:bg-cyan-600 transition-colors"
            >
              Post Problem
            </button>
          </form>
        )}
      </div>

      {/* AI Smart Search */}
      <AISmartSearch
        problems={problems}
        solutions={solutions}
        onResults={setAiResults}
        isActive={aiSearchOpen}
        onToggle={() => setAiSearchOpen((v) => !v)}
      />

      {/* Top bar */}
      <div className="p-4 border-b border-border bg-card space-y-3 shrink-0">
        {/* Search */}
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            placeholder="Search problems, categories, keywords..."
            className="w-full pl-9 pr-4 py-2.5 bg-muted border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors duration-150"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-1">
          {TAB_OPTIONS.map((tab) => (
            <button
              key={`tab-${tab.id}`}
              onClick={() => updateFilter('tab', tab.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors duration-150 ${
                filters.tab === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Trending category pills */}
        <div className="flex flex-wrap gap-1.5">
          <span className="text-xs text-muted-foreground font-medium self-center shrink-0">Trending:</span>
          {TRENDING_CATEGORIES.map((cat) => (
            <button
              key={`trend-${cat}`}
              onClick={() => updateFilter('category', filters.category === cat ? 'All' : cat)}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors duration-150 ${
                filters.category === cat
                  ? 'bg-primary text-primary-foreground'
                  : `${CATEGORY_COLORS[cat] ?? 'bg-gray-100 text-gray-700'} hover:opacity-80`
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Feed */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {/* AI results banner */}
        {aiResults && (
          <div className="px-4 py-2 bg-primary/5 border-b border-primary/20 flex items-center justify-between">
            <span className="text-xs font-semibold text-primary">
              ✨ AI found {aiResults.problems.length + aiResults.solutions.length} result{aiResults.problems.length + aiResults.solutions.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={() => setAiResults(null)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear AI results
            </button>
          </div>
        )}

        {filters.tab === 'solutions' ? (
          /* ── Solutions / Business Proposals list ── */
          displaySolutions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3 p-6">
              <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                <Briefcase size={20} className="text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-foreground">No solutions posted yet</p>
              <p className="text-xs text-muted-foreground text-center max-w-xs">
                Be the first to propose a solution or business plan.
              </p>
            </div>
          ) : (
            <div className="p-3 space-y-3">
              {displaySolutions.map((solution) => (
                <SolutionCard
                  key={solution.id}
                  solution={solution}
                  currentUserId={null}
                  onFundClick={() => {}}
                  onUpdate={() => {}}
                  onDelete={() => {}}
                />
              ))}
            </div>
          )
        ) : displayProblems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3 p-6">
            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
              <Lightbulb size={20} className="text-muted-foreground" />
            </div>
            <p className="text-sm font-semibold text-foreground">No problems match your filters</p>
            <p className="text-xs text-muted-foreground text-center max-w-xs">
              Try adjusting your category, status, or funding range filters to see more results.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {displayProblems.map((problem) => {
              const fundingPct =
                problem.fundingTarget > 0
                  ? Math.round((problem.fundingRaised / problem.fundingTarget) * 100)
                  : 0;
              const isSelected = selectedId === problem.id;

              return (
                <button
                  key={problem.id}
                  onClick={() => onSelect(problem)}
                  className={`w-full text-left p-4 transition-colors duration-150 ${
                    isSelected
                      ? 'bg-primary/5 border-l-2 border-l-primary' :'hover:bg-muted/50 border-l-2 border-l-transparent'
                  }`}
                >
                  {/* Category + Status */}
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${problem.categoryColor}`}>
                      {problem.category}
                    </span>
                    <StatusBadge variant={problem.status} size="sm" />
                  </div>

                  {/* Title */}
                  <p className={`text-sm font-bold leading-snug mb-1.5 line-clamp-2 ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                    {problem.title}
                  </p>

                  {/* Problem Image */}
                  {problem.image && (
                    <div className="mb-2 rounded-lg overflow-hidden h-28 bg-muted">
                      <img
                        src={problem.image}
                        alt={problem.imageAlt || problem.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Excerpt */}
                  <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed mb-2.5">
                    {problem.excerpt}
                  </p>

                  {/* Funding bar */}
                  {problem.fundingRaised > 0 && (
                    <div className="mb-2.5">
                      <div className="h-1 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${fundingPct >= 100 ? 'bg-success' : 'bg-primary'}`}
                          style={{ width: `${Math.min(fundingPct, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 tabular-nums">
                        ${(problem.fundingRaised / 1000).toFixed(0)}K of ${(problem.fundingTarget / 1000).toFixed(0)}K
                      </p>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Lightbulb size={11} />
                      {problem.solutions.length}
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageSquare size={11} />
                      {problem.comments.length}
                    </span>
                    <span className="flex items-center gap-1">
                      <Bookmark size={11} />
                      {problem.bookmarks}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users size={11} />
                      {problem.followers}
                    </span>
                    <span className="ml-auto font-medium">{problem.postedAt}</span>
                  </div>

                  {/* View Details link — only for DB-persisted problems (UUID format) */}
                  {/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(problem.id) && (
                    <Link
                      href={`/problem/${problem.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-2.5 w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold text-primary hover:text-cyan-600 border border-primary/20 hover:border-primary/50 rounded-lg transition-colors"
                    >
                      <ExternalLink size={11} /> View Full Details
                    </Link>
                  )}

                  {/* Propose a Solution button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!user) {
                        router.push('/sign-up-login-screen');
                        return;
                      }
                      onProposeSolutionForProblem(problem);
                    }}
                    className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold text-white bg-primary hover:bg-cyan-600 rounded-lg transition-colors"
                  >
                    <Briefcase size={11} /> Propose a Solution
                  </button>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
export { CATEGORY_COLORS };